import { Col, Row } from "antd";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { EBButtonV2 } from "../components/common/EBButtonV2";
import { EBTextInput } from "../components/common/eb-ui-components";
import { FormikQuestionDifficultySelect } from "../components/common/input/FormikQuestionDifficultySelect";
import { SearchTokenHelper } from "../helpers/Search.helper";
import { WindowHelpers } from "../helpers/Window.helper";
import { SearchToken } from "../model/search/SearchToken";
import { SearchTokenName } from "../model/search/SearchTokenName";
import { SearchTokenDataDifficultyLevel } from "../model/search/data/SearchTokenDataDifficultyLevel";
import { SearchTokenDataSearchQueryString } from "../model/search/data/SearchTokenDataSearchQueryString";
import { SearchTokenDataShowMine } from "../model/search/data/SearchTokenDataShowMine";
import { SearchTokenTopic } from "../model/search/data/SearchTokenTopic";
import { SearchProps } from "../search/Search.Model";
import { Topic, TopicType } from "../topic/Topic.model";
import { TopicAPI } from "../topic/TopicAPI";
import { TopicSelector } from "../topic/TopicSelector.component";
import { QuestionDifficulty } from "./Question.Model";

interface QuestionFilterValues {
  companies: Topic[];
  difficultyFilter: QuestionDifficulty[];
  topics: Topic[];
  searchQuery?: string;
  showMine: boolean;
}

export function QuestionsFilterView(filterProps: SearchProps) {
  let [topics, setTopics] = useState<Topic[]>();
  let [allCompanyTopics, setAllCompanyTopics] = useState<Topic[]>();
  useEffect(() => {
    TopicAPI.getListingTopics().then((topics) => {
      const filteredtopics = topics.filter(
        (topic) => topic.topicType == TopicType.SKILL
      );

      const companyTopics = topics.filter(
        (topic) => topic.topicType == TopicType.COMPANY
      );

      setTopics(filteredtopics);
      setAllCompanyTopics(companyTopics);
    });
  }, []);

  let initialValues: QuestionFilterValues = convertTokenToValues(
    filterProps.initialStartingFilterToken
  );

  return (
    <div className="QuestionsFilterView card bg-transparent mb-3">
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => {
          return filterProps.onSearch([], true, convertValuesToToken(values));
        }}
      >
        {(props) => (
          <Form>
            <div className="">
              <Row align={"middle"} gutter={[20, 20]} className="mt-3 mx-2">
                <Col flex={"auto"}>
                  <FormikQuestionDifficultySelect
                    isMulti={true}
                    label=""
                    name="difficultyFilter"
                    placeholder="Select difficulty level to filter"
                  />
                </Col>
                <Col flex={"auto"} className="mb-3">
                  {topics && (
                    <TopicSelector
                      placeholder="Select topic to filter"
                      label=""
                      topics={topics}
                      name={"topics"}
                      isMulti={true}
                    />
                  )}
                </Col>
                <Col flex={"auto"} className="mb-3">
                  {allCompanyTopics && (
                    <TopicSelector
                      label=""
                      name="companies"
                      isMulti={true}
                      topics={allCompanyTopics}
                      placeholder="Select company to filter"
                    />
                  )}
                </Col>
                <Col flex={"auto"}>
                  <EBTextInput
                    label=""
                    name="searchQuery"
                    placeholder="Enter search query here"
                    helpText=""
                  />
                </Col>
              </Row>
              <Row align={"middle"} gutter={[20, 20]} className="mx-2 mb-3">
                <Col flex={"auto"}></Col>
                <Col>
                  {filterProps.user && (
                    <>
                      <Field
                        type="checkbox"
                        id="showMine"
                        className="custom-control-input"
                        name="showMine"
                      />

                      <label
                        className="custom-control-label"
                        htmlFor="showMine"
                      >
                        Show only mine
                      </label>
                    </>
                  )}
                </Col>
                <Col>
                  <EBButtonV2
                    className="btn btn-secondary"
                    content={{
                      normal: props.isSubmitting
                        ? filterProps.filterBtnProps?.content.submitting ??
                          "Please Wait..."
                        : filterProps.filterBtnProps?.content.normal ??
                          "Filter",
                    }}
                    disabled={props.isSubmitting}
                    type="submit"
                  />
                </Col>
              </Row>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

function convertValuesToToken(values: QuestionFilterValues): SearchToken {
  let searchTokens: SearchToken[] = [];
  searchTokens.push(SearchTokenHelper.getShowMineToken(values.showMine));
  if (values.companies && values.companies.length > 0) {
    let topicTokenData: SearchTokenTopic = {
      topics: values.companies,
    };
    searchTokens.push(SearchTokenHelper.getTopicToken(topicTokenData));
  }
  if (values.difficultyFilter && values.difficultyFilter.length > 0) {
    let difficultyTokenData: SearchTokenDataDifficultyLevel = {
      difficulties: values.difficultyFilter,
    };
    searchTokens.push(
      SearchTokenHelper.getDifficultyLevelToken(difficultyTokenData)
    );
  }
  if (values.topics && values.topics.length > 0) {
    let topicTokenData: SearchTokenTopic = {
      topics: values.topics,
    };
    searchTokens.push(SearchTokenHelper.getTopicToken(topicTokenData));
  }
  if (values.searchQuery) {
    searchTokens.push(
      SearchTokenHelper.getSearchStringToken(values.searchQuery)
    );
  }

  return SearchTokenHelper.getAndToken(searchTokens);
}

function convertTokenToValues(
  startingToken?: SearchToken
): QuestionFilterValues {
  let initialValues: QuestionFilterValues = {
    companies: [] as Topic[],
    difficultyFilter: [] as QuestionDifficulty[],
    topics: [] as Topic[],
    searchQuery: undefined as string | undefined,
    showMine: WindowHelpers.qParam("my") === "true",
  };
  if (!startingToken) {
    return initialValues;
  }
  // Right now the startingFilterToken is always an AND token
  const andTokenData = startingToken.data as {
    tokens: SearchToken[];
  };
  for (var st of andTokenData.tokens) {
    if (st.name === SearchTokenName.SHOW_MINE) {
      initialValues.showMine = (st.data as SearchTokenDataShowMine).showMine;
    }

    if (st.name === SearchTokenName.TOPIC) {
      let topics = (st.data as SearchTokenTopic).topics;
      if (topics) {
        for (var topic of topics) {
          if (topic.topicType === TopicType.COMPANY) {
            initialValues.companies.push(topic);
          } else {
            initialValues.topics.push(topic);
          }
        }
      }
    }

    if (st.name === SearchTokenName.DIFFICULTY_LEVEL) {
      initialValues.difficultyFilter = (
        st.data as SearchTokenDataDifficultyLevel
      ).difficulties;
    }

    if (st.name === SearchTokenName.SEARCH_QUERY_STRING) {
      initialValues.searchQuery = (
        st.data as SearchTokenDataSearchQueryString
      ).searchQuery;
    }
  }
  return initialValues;
}
